import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

/** 全局告警横幅数据结构 */
export interface GlobalAlert {
  id: string
  level: 'critical' | 'warning' | 'info'
  title: string
  detail?: string
  route?: string      // 点击跳转路由
  timestamp: Date
  dismissed: boolean
}

/** 告警等级优先级映射 */
const LEVEL_PRIORITY: Record<GlobalAlert['level'], number> = {
  critical: 0,
  warning: 1,
  info: 2,
}

export const useAlertBannerStore = defineStore('alertBanner', () => {
  const alerts = ref<GlobalAlert[]>([])

  /** 未关闭的告警列表，按优先级排序（critical > warning > info） */
  const activeAlerts = computed(() =>
    alerts.value
      .filter(a => !a.dismissed)
      .sort((a, b) => LEVEL_PRIORITY[a.level] - LEVEL_PRIORITY[b.level])
  )

  /** 是否有未关闭的告警 */
  const hasAlerts = computed(() => activeAlerts.value.length > 0)

  /** 新增告警 */
  function addAlert(alert: Omit<GlobalAlert, 'id' | 'timestamp' | 'dismissed'>): void {
    const id = `alert-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    alerts.value.push({ ...alert, id, timestamp: new Date(), dismissed: false })
  }

  /** 关闭指定告警 */
  function dismissAlert(id: string): void {
    const alert = alerts.value.find(a => a.id === id)
    if (alert) alert.dismissed = true
  }

  /** 关闭所有告警 */
  function dismissAll(): void {
    alerts.value.forEach(a => { a.dismissed = true })
  }

  /** 清空告警列表 */
  function clearAll(): void {
    alerts.value = []
  }

  return { alerts, activeAlerts, hasAlerts, addAlert, dismissAlert, dismissAll, clearAll }
})
